/* eslint-disable no-console */

/**
 * Auto-approve (user decision 2026-10-06): a monitored device the owner
 * already approved on the Devices page does not need a second approval when
 * it adopts the new encryption. UploaderAutoApproveRule.js has the rule;
 * this is the glue the owner's unlocked browser runs from the link-request
 * watch (App.vue, once a minute) and the /link-device page load:
 *
 *   1. the registered devices, fetched NOW off the account row (the store's
 *      copy can be days old; same source and same "never guess" rule as the
 *      device-list sweep in DeviceListSync.js);
 *   2. the signed device list, fetched NOW;
 *   3. select; then approve ONE AT A TIME - every approval re-reads the list
 *      and the log and appends a chain entry, so two in flight would race
 *      each other to a 409.
 *
 * Only a key change for a device already on the list stays manual (the rule
 * leaves those out). Idempotent and serialized per owner. Never throws.
 */
import E2eeApi from '@/aws/e2ee/E2eeApi'
import api from '@/lib/api'
import { E2EE_ERROR_CODES, classifyE2EEError } from '../E2EEErrors'
import { hasCachedIdentity } from './Unlock'
import { approveRequest, legacyPendingDeviceUuids } from './DeviceLink'
import { deviceUuidFromLegacyId } from './DeviceRetirement'
import { selectAutoApprovableUploaders } from './UploaderAutoApproveRule'

export { selectAutoApprovableUploaders }

const inFlight = new Map() // ownerEmail -> Promise<result>

const norm = (e) => String(e || '').toLowerCase()

const isCandidate = (r) => r && r.purpose === 'uploader' && r.state === 'pending' && r.devSignPk && r.deviceUuid

/**
 * The complete set of device uuids registered on the account right now, or
 * null when the answer cannot be trusted (account unreadable, an id this
 * code cannot decode). Mirrors the sweep in DeviceListSync.js.
 */
async function fetchRegisteredUuids (ownerEmail) {
  const resp = await api.getAccountAwait(false)
  const data = resp && resp.status === 200 && resp.data
  const identities = data && data.account && data.account.identities
  if (!data || norm(data.accountId) !== ownerEmail || !Array.isArray(identities)) return null
  const registered = []
  for (const ident of identities) {
    const id = ident && ident.id
    // A login that never sent a device UUID has a bare id (no '|'); it can
    // never have posted an uploader enrollment, so it is simply not a candidate.
    if (typeof id === 'string' && id && !id.includes('|')) continue
    const uuid = deviceUuidFromLegacyId(id)
    if (!uuid) return null
    registered.push(uuid)
  }
  return registered
}

/**
 * @param {{email: string, requests: Array, storeAccount: *}} args
 *   requests      the pending enrollment requests the caller already fetched
 *   storeAccount  `$store.state.account` (for the still-pending legacy adds)
 * @returns {Promise<{approved: string[], skipped: string[], errors: {requestId: string, message: string}[], unlockRequired: boolean}>}
 *   `approved` holds the requestIds no longer pending (approved here, or
 *   found already accepted elsewhere); callers drop those from their lists.
 */
export async function autoApproveRegisteredUploaders ({ email, requests, storeAccount }) {
  const ownerEmail = norm(email)
  if (inFlight.has(ownerEmail)) return inFlight.get(ownerEmail)
  const run = autoApprove(ownerEmail, requests || [], storeAccount).finally(() => inFlight.delete(ownerEmail))
  inFlight.set(ownerEmail, run)
  return run
}

async function autoApprove (ownerEmail, requests, storeAccount) {
  const result = { approved: [], skipped: [], errors: [], unlockRequired: false }
  const candidates = requests.filter(isCandidate)
  try {
    if (!E2eeApi.isConfigured() || !ownerEmail || candidates.length === 0) return result
    if (!(await hasCachedIdentity(ownerEmail))) {
      result.unlockRequired = true
      return result
    }
    const registeredUuids = await fetchRegisteredUuids(ownerEmail)
    if (!registeredUuids) {
      console.log('nc1 auto-approve: skipped, the account\'s registered devices could not be read')
      return result
    }
    const deviceList = (await E2eeApi.getDeviceList(ownerEmail)).data
    const pendingLegacyUuids = legacyPendingDeviceUuids(storeAccount)
    const selected = selectAutoApprovableUploaders(candidates, { registeredUuids, pendingLegacyUuids, deviceList })
    const chosen = new Set(selected)
    for (const r of candidates) if (!chosen.has(r)) result.skipped.push(r.requestId)

    for (const request of selected) {
      try {
        await approveRequest({ email: ownerEmail, request })
        console.log(`nc1 auto-approve: approved ${request.deviceName || request.deviceUuid} (${request.requestId}), a device already registered on the account`)
        result.approved.push(request.requestId)
      } catch (e) {
        if (classifyE2EEError(e).code === E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED) {
          result.unlockRequired = true
          break
        }
        if (e && e.response && e.response.status === 409) {
          // Accepted elsewhere (another tab, the dashboard dialog) between our
          // list and our accept: no longer pending either way.
          console.log(`nc1 auto-approve: ${request.requestId} was already accepted elsewhere`)
          result.approved.push(request.requestId)
          continue
        }
        console.log(`nc1 auto-approve: ${request.requestId} failed`, e)
        result.errors.push({ requestId: request.requestId, message: (e && e.message) || String(e) })
      }
    }
  } catch (e) {
    if (classifyE2EEError(e).code === E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED) result.unlockRequired = true
    else {
      console.log('nc1 auto-approve failed', e)
      result.errors.push({ requestId: '*', message: (e && e.message) || String(e) })
    }
  }
  return result
}
